import React, { useEffect, useState, useRef } from "react";
import { Card, CardBody, CardHeader, InputGroupText, Row, Col, Label } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import parse from "html-react-parser";
import Autocomplete from "react-google-autocomplete";
import { Map, GoogleApiWrapper, Marker } from 'google-maps-react'; //https://www.digitalocean.com/community/tutorials/how-to-integrate-the-google-maps-api-into-react-applications
import moment from "moment";

import { useRecoilState } from "recoil";
import { accessTokenState, accountState, controlState, userState, } from "../../Shared/atom";

import AuthenticateUser from "../common/AuthenticateUser.Component";
import Input from "../common/Input.Component";
import ModalPrompt from "../common/ModalPrompt.Component";
import PageLoading from "../common/PageLoading.Component";

import commonHelper from "../../Shared/commonHelper";
import emailHelper from "../../Shared/emailHelper";
import message from "../../Shared/message";
import phoneHelper from "../../Shared/phoneHelper";
import restHelper from "../../Shared/restHelper";
import sessionStore from "../../Shared/sessionStore";
import { storageKeys } from "../../Shared/enums";
import useFullPageLoader from "../../Hooks/useFullPageLoader";

import businessDetailsIcon from "../../Images/profile-business-details.png"
import siteContactIcon from "../../Images/profile-site-contact.png"
import siteAddressIcon from "../../Images/profile-site-address.png"
import billingContactIcon from "../../Images/profile-billing-contact.png"
import billingAddressIcon from "../../Images/profile-billing-address.png"

import styles from "./Profile.module.scss";
import useUnsavedChangesWarning from "../../Hooks/useUnsavedChangesWarning";

import useHttp from "../../Hooks/useHttp";
import * as apiUrls from "../../Shared/endPoints";

const ProfilePage = props => {
    // -- Global State --------------------------------------------------------------------------------------------------------------------
    const [accessToken] = useRecoilState(accessTokenState);
    const [controlSize] = useRecoilState(controlState);
    const [account, accountSet] = useRecoilState(accountState);
    const [portalUser] = useRecoilState(userState);

    // -- Local State ---------------------------------------------------------------------------------------------------------------------
    const autoCompleteRef = useRef(null);

    const [accessFromInvalidId, accessFromInvalidIdSet] = useState(null);
    const [accessFromMsgId, accessFromMsgIdSet] = useState(null);
    const [accessToInvalidId, accessToInvalidIdSet] = useState(null);
    const [accessToMsgId, accessToMsgIdSet] = useState(null);
    const [accessWindowMsgId, accessWindowMsgIdSet] = useState(null);
    const [industryCodes, industryCodesSet] = useState(null);
    const [localAccount, localAccountSet] = useState(null);
    const [pageError, pageErrorSet] = useState(null);
    const [pageLoaded, pageLoadedSet] = useState(null);
    const [saveProfileMsgId, saveProfileMsgIdSet] = useState(null);
    const [siteAddressMsgId, siteAddressMsgIdSet] = useState(null);
    const [siteAddressIgnoreNum, siteAddressIgnoreNumSet] = useState(false);
    const [siteStreetNumMsgId, siteStreetNumMsgIdSet] = useState(null);

    const [cancelTitle, cancelTitleSet] = useState(null);
    const [cancelMessage, cancelMessageSet] = useState(null);
    const [displayCancel, displayCancelSet] = useState(false);

    const [loader, showLoader, hideLoader] = useFullPageLoader();
    const [mapCenter, mapCenterSet] = useState();

    const [NavigationPrompt, navigationBlockedSet, navigationUnBlockedSet] = useUnsavedChangesWarning();
    const [isDirty, isDirtySet] = useState(false);

    const [businessReadOnly, businessReadOnlySet] = useState(true);
    const [siteContactReadOnly, siteContactReadOnlySet] = useState(true);
    const [siteAddressReadOnly, siteAddressReadOnlySet] = useState(true);
    const [billingContactReadOnly, billingContactReadOnlySet] = useState(true);
    const [billingAddressReadOnly, billingAddressReadOnlySet] = useState(true);
    const [cancelGroup, cancelGroupSet] = useState(null);

    const [abnPrevious, abnPreviousSet] = useState();

    const { request: abnValidationRequest } = useHttp("Validating ABN", true);
    const { request: getIndustryCodesData } = useHttp("Industry Codes");
    const { request: postAuditHistory } = useHttp();
    const { request: updateAccount } = useHttp("Account");

    const BILLING_ADDRESS = "Billing Address";
    const BILLING_CONTACT = "Billing Contact";
    const BUSINESS = "Business";
    const SITE_ADDRESS = "Site Address";
    const SITE_CONTACT = "Site Contact";

    useEffect(() => {
        document.title = "Shred-X: Profile"
    }, []);

    useEffect(() => {
        if (portalUser && accessToken.token) {
            postAuditHistory(apiUrls.AddAuditEntry, "post", JSON.stringify({ "page": "Profile", "action": "View", "detail": null }));
        }
    }, [accessToken, portalUser, postAuditHistory]);

    useEffect(() => {
        if (accessToken.token && portalUser && !industryCodes) {
            const storedIndustryCodes = sessionStore.getItem(storageKeys.INDUSTRY_CODES);

            if (storedIndustryCodes) {
                industryCodesSet(storedIndustryCodes)
            }

            else {
                getIndustryCodesData(apiUrls.GetIndustryCodes).then(data => {
                    data.sort((a, b) => a.label.localeCompare(b.label));
                    industryCodesSet(data);
                    sessionStore.setItem(storageKeys.INDUSTRY_CODES, data);
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, portalUser])

    useEffect(() => {
        const loadPage = async () => {
            try {
                const _portalAccount = commonHelper.thaw(account)
                setDefaults(_portalAccount);
                localAccountSet(_portalAccount);

                abnPreviousSet(_portalAccount.abn)
                centerMap(_portalAccount);

                pageLoadedSet(true);
            }

            catch (ex) {
                let errMsg = ex?.message || ex;

                while (errMsg.indexOf("\n") > -1) {
                    errMsg = errMsg.replace("\n", "<br/>");
                }

                pageErrorSet(parse(errMsg));
                pageLoadedSet(false);
            }
        }

        if (accessToken.token && account && portalUser) loadPage();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, account, portalUser]);

    // -- Form Functions ------------------------------------------------------------------------------------------------------------------

    const cancelUpdate = () => {
        displayCancelSet(false);
        const _portalAccount = commonHelper.thaw(account);
        setDefaults(_portalAccount);
        centerMap(account);
        autoCompleteRef.current.value = null;
        navigationUnBlockedSet();
        isDirtySet(false);
        localAccountSet(_portalAccount);
        toggleReadOnlyGroup(cancelGroup, true);
    }

    const centerMap = (_portalAccount) => {
        if (_portalAccount.siteAddress && _portalAccount.siteAddress.latitude && _portalAccount.siteAddress.longitude) {
            const center = new window.google.maps.LatLng(_portalAccount.siteAddress.latitude, _portalAccount.siteAddress.longitude);
            mapCenterSet(center);
        }
    }

    const displayCancelPrompt = (evt, group) => {
        evt.stopPropagation();

        if (isDirty) {
            cancelGroupSet(group);
            cancelTitleSet(`Update ${group} Details`);
            cancelMessageSet(`Discard any changes made to the ${group.toLowerCase()} details?`);
            displayCancelSet(true);
        }

        else {
            toggleReadOnlyGroup(group, true);
        }
    }

    const handleChange = (evt) => {
        const control = evt.target.id.split(".");
        let value = evt.target.value;

        const _localAccount = commonHelper.deepClone(localAccount)

        if (control.length === 1) {
            _localAccount[control[0]] = value;

            if (control[0] === "abn" && value) {
                _localAccount.noAbn = false;
            }

            else if (control[0] === "noAbn") {
                _localAccount[control[0]] = evt.target.checked;

                if (evt.target.checked) {
                    _localAccount.abn = null;

                    if (_localAccount.errors && _localAccount.errors.abn) {
                        _localAccount.errors.abn = null;
                    }
                }

                else {
                    _localAccount.errors.abn = "This is a required field";
                }
            }

            else {
                _localAccount[control[0]] = value;
            }
        }

        else if (control.length === 2) {
            if (control[1] === "preferredNumber" && value.length > 0) {
                // Ensure the value contains valid characters for a phone number...
                if (phoneHelper.hasValidCharacters(value)) _localAccount[control[0]][control[1]] = value;
            }

            else if (control[1] === "notFound") {
                // The address not found checkbox...
                _localAccount[control[0]][control[1]] = evt.target.checked;
            }

            else {
                _localAccount[control[0]][control[1]] = value;
            }
        }

        localAccountSet(_localAccount)
        navigationBlockedSet();
        isDirtySet(true);
    }

    const handleFocus = (evt) => evt.target.select();

    const hasErrors = group => {
        if (!localAccount.errors) {
            return false;
        }

        switch (group) {
            case BUSINESS:
                return localAccount.errors.accountName || localAccount.errors.accountNumber || localAccount.errors.abn || localAccount.errors.industryCode;
            default:
        }
    }

    const validateSiteAddress = (_siteAddress) => {
        let _saveProfileMsgId = saveProfileMsgId;

        const siteAddressValid = isValidSiteAddress(_siteAddress);

        const siteAccessValid = isValidSiteAccess("openingTime", localAccount.openingTime, localAccount.closingTime)
            && isValidSiteAccess("closingTime", localAccount.openingTime, localAccount.closingTime);

        if (!siteAddressValid || !siteAccessValid) {
            _saveProfileMsgId = message.Warning("Save Site Address", "The site address details contain invalid data.\n\nPlease correct the issues and try again.")
            return false;
        }

        else {
            if (_saveProfileMsgId) {
                message.Dismiss(_saveProfileMsgId);
                _saveProfileMsgId = null;
                saveProfileMsgIdSet(_saveProfileMsgId);
            }

            return true;
        }
    }

    const sendNotFoundEmails = async (msgTitle) => {
        let emailModel;
        let emailsSent = true;

        try {
            emailModel = {
                templateName: "Address Assist - Customer",
                firstName: portalUser.givenName,
                lastName: portalUser.surname,
                senderEmail: portalUser.email,
                toAddress: portalUser.email,
                accountId: portalUser.accountId,
                formData: {
                    contactFirstName: portalUser.givenName
                },
            };

            await restHelper.sendGridEmail(emailModel, accessToken.token);
        }

        catch (ex) {
            message.Warning("Send Email Notification", ex);
            emailsSent = false;
        }

        try {
            emailModel.templateName = "Address Assist - Customer Service";
            emailModel.formData = {
                can: localAccount.accountNumber,
                client: localAccount.accountName,
                submittedBy: `${portalUser.givenName} ${portalUser.surname}`,
                submittedByEmail: portalUser.email,
                submittedDtTm: moment().format("ddd, D MMM YYYY"),
                preferredNumber: localAccount.billingContact.preferredNumber,
            };

            await restHelper.sendGridEmail(emailModel, accessToken.token);
        }

        catch (ex) {
            message.Warning("Send Email Notification", ex.message ?? ex);
            emailsSent = false;
        }

        if (emailsSent) {
            message.Information(msgTitle, "A member of our Customer Service team will contact you shortly to assist.");
            return true;
        }
    }

    const saveProfile = async (evt, group) => {
        evt.stopPropagation();

        showLoader();

        const msgTitle = `Save ${group} Details`;
        let isSaved = false;

        try {
            switch (group) {
                case BUSINESS:
                    if (!localAccount.accountName) {
                        message.Warning(msgTitle, "Company Name is a required field.");
                        return;
                    }

                    if (!localAccount.abn && !localAccount.noAbn) {
                        message.Warning(msgTitle, "An ABN is required unless \"Continue without ABN\" is selected.");
                        return;
                    }

                    if (!localAccount.industryCodeId) {
                        message.Warning(msgTitle, "Industry Type is a required field.");
                        return;
                    }

                    break;

                case BILLING_CONTACT:
                    if (!localAccount.billingContact.preferredNumber) {
                        message.Warning(msgTitle, "Please provide a phone number for the billing contact.");
                        return;
                    }

                    // Ensure the phone numbers are valid...
                    if (localAccount.billingContact.preferredNumber && !phoneHelper.isValid(localAccount.billingContact.preferredNumber)) {
                        message.Warning(msgTitle, "The preferred billing contact number does not appear to be valid.");
                        return;
                    }

                    break;

                case SITE_CONTACT:
                    if (!localAccount.siteContact.preferredNumber) {
                        message.Warning(msgTitle, "Please provide a phone number for the site contact.");
                        return;
                    }

                    // Ensure the phone numbers are valid...
                    if (localAccount.siteContact.preferredNumber && !phoneHelper.isValid(localAccount.siteContact.preferredNumber)) {
                        message.Warning(msgTitle, "The preferred site number does not appear to be valid.");
                        return;
                    }

                    break;

                case SITE_ADDRESS:
                    let _siteAddress = localAccount.siteAddress;

                    const isValid = validateSiteAddress(_siteAddress);
                    if (!isValid) return;

                    // If the address couldn't be found then email the user and customer service...
                    if (_siteAddress.notFound) {
                        sendNotFoundEmails(msgTitle);

                        // Set the site access controls read-only...
                        toggleReadOnlyGroup(group, true);

                        // Reset the dirty and navigation flags...
                        isDirtySet(false);
                        navigationUnBlockedSet();

                        // Don't save any updates...
                        cancelUpdate();
                        return;
                    }

                    break;

                default:
                    break;
            }

            updateAccount(apiUrls.UpdateAccount, "post", JSON.stringify(localAccount))
                .then((result) => {
                    if (result.updated) {
                        accountSet(localAccount);

                        if (group === BUSINESS) {
                            message.Success(msgTitle, "The business details were successfully updated.");
                        }

                        else {
                            message.Success(msgTitle, `${group} successfully updated.`);
                        }
                    }

                    else {
                        message.Information(msgTitle, "There were no updates to the existing data.");
                    }

                    isSaved = true;
                })
                .catch(ex => {
                    message.Error(msgTitle, ex.message ?? ex);
                })
                .finally(() => {
                    if (isSaved) {
                        toggleReadOnlyGroup(group, true);
                        isDirtySet(false);
                        navigationUnBlockedSet();
                    }
                });
        }

        catch (ex) {
            message.Error(msgTitle, "An error occurred updating the Profile details.\n\nPlease refresh the page and if the error persists contact Customer Service on 1300&nbsp;SHREDX (1300&nbsp;747&#8209;339) for assistance.");
        }

        finally {
            hideLoader();
        }
    }

    const setDefaults = (_portalAccount) => {
        // -- Site Contact
        const _siteContact = _portalAccount.siteContact;

        // The CRM originally only displayed a single telephone field so users would enter 2 (or more) numbers separated by "/"...
        if (_siteContact.preferredNumber.includes("/")) {
            const numbers = _siteContact.preferredNumber.split("/");
            _siteContact.preferredNumber = numbers[0];
        }

        _siteContact.preferredNumber = phoneHelper.format(_siteContact.preferredNumber);

        // -- Billing Contact
        const _billingContact = _portalAccount.billingContact;

        if (_billingContact.preferredNumber.includes("/")) {
            const numbers = _billingContact.preferredNumber.split("/");
            _billingContact.preferredNumber = numbers[0];
            // if (!_billingContact.alternateNumber) _billingContact.alternateNumber = numbers[1];
        }

        _billingContact.preferredNumber = phoneHelper.format(_billingContact.preferredNumber);

        // -- Bin Access Times
        // The opening and closing times should be formatted as hh:mm...
        if (_portalAccount?.openingTime?.length > 0 && _portalAccount?.openingTime?.length < 5) {
            let time = _portalAccount.openingTime.split(":");
            time[0] = time[0].padStart(2, "0");
            time[1] = time[1].padStart(2, "0");
            _portalAccount.openingTime = time.join(":");
        }

        if (_portalAccount?.closingTime?.length > 0 && _portalAccount?.closingTime?.length < 5) {
            let time = _portalAccount.closingTime.split(":");
            time[0] = time[0].padStart(2, "0");
            time[1] = time[1].padStart(2, "0");
            _portalAccount.closingTime = time.join(":");
        }
    }

    const toggleReadOnlyGroup = (group, readOnly) => {
        switch (group) {
            case BUSINESS:
                businessReadOnlySet(readOnly);
                break;
            case SITE_CONTACT:
                siteContactReadOnlySet(readOnly);
                break;
            case SITE_ADDRESS:
                siteAddressReadOnlySet(readOnly);
                break;
            case BILLING_CONTACT:
                billingContactReadOnlySet(readOnly);
                break;
            case BILLING_ADDRESS:
                billingAddressReadOnlySet(readOnly);
                break;
            default:
        }
    }

    const isValidSiteAccess = (controlId, accessFrom, accessTo) => {
        let _accessFromInvalidId = accessFromInvalidId;
        let _accessFromMsgId = accessFromMsgId;

        let _accessToInvalidId = accessToInvalidId;
        let _accessToMsgId = accessToMsgId;

        let _accessWindowMsgId = accessWindowMsgId;

        // If the user selects the access fields multiple times, and the data they contained was invalid, than an error message was created each time and, if they didn't close the message
        // each time,  the messages would just stack up on the screen.  By storing the ID returned from each call to display the message we can prevent duplicate messages being displayed.
        if (controlId === "openingTime") {
            if (!accessFrom) {
                _accessFromMsgId = message.Warning("Bins Accessible From", "You must enter the time the bin(s) will be accessible", _accessFromMsgId);
                accessFromMsgIdSet(_accessFromMsgId);
                return false;
            }

            else {
                if (_accessFromMsgId) {
                    message.Dismiss(_accessFromMsgId);
                    _accessFromMsgId = null;
                    accessFromMsgIdSet(_accessFromMsgId);
                }
            }

            const openHours = accessFrom.split(":");

            if (!(openHours.length === 2 && !isNaN(openHours[0], 10) && !isNaN(openHours[1], 10))) {
                _accessFromInvalidId = message.Warning("Bins Accessible From", "The time entered appears to be invalid.", _accessFromInvalidId);
                accessFromInvalidIdSet(_accessFromInvalidId);
                return false;
            }

            else {
                if (_accessFromInvalidId) {
                    message.Dismiss(_accessFromInvalidId);
                    _accessFromInvalidId = null;
                    accessFromInvalidIdSet(_accessFromInvalidId);
                }
            }
        }

        if (controlId === "closingTime") {
            if (!accessTo) {
                _accessToMsgId = message.Warning("Bins Accessible To", "You must enter the time the bin(s) will no longer be accessible", _accessToMsgId);
                accessToMsgIdSet(_accessToMsgId);
                return false;
            }

            else {
                if (_accessToMsgId) {
                    message.Dismiss(_accessToMsgId);
                    _accessToMsgId = null;
                    accessToMsgIdSet(_accessToMsgId);
                }
            }

            const closeHours = accessTo.split(":");

            if (!(closeHours.length === 2 && !isNaN(closeHours[0], 10) && !isNaN(closeHours[1], 10))) {
                _accessToInvalidId = message.Warning("Bins Accessible To", "The time entered appears to be invalid.", _accessToInvalidId);
                accessToInvalidIdSet(_accessToInvalidId);
                return false;
            }

            else {
                if (_accessToInvalidId) {
                    message.Dismiss(_accessToInvalidId);
                    _accessToInvalidId = null;
                    accessToInvalidIdSet(_accessToInvalidId);
                }
            }
        }

        if (accessFrom && accessTo) {
            let now = new Date();

            const openingHours = accessFrom.split(":");
            const closingHours = accessTo.split(":");

            const openingTime = now.setHours(openingHours[0], openingHours[1]);
            let closingTime = now.setHours(closingHours[0], closingHours[1]);

            // If the closing time is less than the opening time than presume the closing time is the following day...
            if (closingTime < openingTime) {
                closingTime = new Date(closingTime);
                closingTime.setDate(closingTime.getDate() + 1);
                closingTime = closingTime.getTime();
            }

            else {
                // 2022-04-27: Nigel Opbroek verified minimum access window should be 3 hours but the task (https://sxdev.visualstudio.com/Customer%20Portal%20-%20Testing/_backlogs/backlog/Customer%20Portal%20-%20Testing%20Team/Features/?workitem=656)
                // stipulated 4 hours...
                const oneHour = 1000 * 60 * 60;

                if ((closingTime - openingTime) < oneHour * 4) {
                    _accessWindowMsgId = message.Warning("Accessible Hours", "You must allow at least a 4 hour window for our driver to access the bins.", _accessWindowMsgId);
                    accessWindowMsgIdSet(_accessWindowMsgId);
                    return false;
                }

                else {
                    if (_accessWindowMsgId) {
                        message.Dismiss(_accessWindowMsgId);
                        _accessWindowMsgId = null;
                        accessToMsgIdSet(_accessWindowMsgId);
                    }
                }
            }
        }

        return true;
    }

    const isValidSiteAddress = (_siteAddress) => {
        let _siteAddressMsgId = siteAddressMsgId;
        let _siteStreetNumMsgId = siteStreetNumMsgId;

        // If the user can't locate their address then return true...
        if (_siteAddress?.notFound) return true;

        if (!_siteAddress.line1 || !_siteAddress.suburb || !_siteAddress.state || !_siteAddress.postcode) {
            _siteAddressMsgId = message.Warning(SITE_ADDRESS, "The address appears to be invalid.\n\nPlease ensure the address contains the street name and number, suburb, state and postcode.", _siteAddressMsgId);
            siteAddressMsgIdSet(_siteAddressMsgId);
        }

        else {
            if (_siteAddressMsgId) {
                message.Dismiss(_siteAddressMsgId);
                _siteAddressMsgId = null;
                siteAddressMsgIdSet(_siteAddressMsgId);
            }
        }

        if (_siteAddress.line1) {
            // If the street number hasn't been provided then warn the client but don't treat it as an error.
            // If the client enters a new address then siteAddressIgnoreNum is reset to false in the AutoComplete component.
            const hasStreetNum = !isNaN(parseInt(_siteAddress.line1, 10));

            if (!hasStreetNum && !siteAddressIgnoreNum) {
                _siteStreetNumMsgId = message.Warning(SITE_ADDRESS, "It appears you haven't included a street number.\n\nIf this is correct then ignore this message otherwise enter a street number.", _siteStreetNumMsgId)
                siteStreetNumMsgIdSet(_siteStreetNumMsgId);
                siteAddressIgnoreNumSet(true);
            }

            else {
                if (_siteStreetNumMsgId) {
                    message.Dismiss(_siteStreetNumMsgId);
                    _siteStreetNumMsgId = null;
                    siteStreetNumMsgIdSet(_siteStreetNumMsgId);
                }
            }
        }

        return !_siteAddressMsgId;
    }

    const validateInput = async (evt) => {
        const tgt = evt.target;
        const value = tgt.value.trim();
        const control = tgt.id.split(".");

        let err = null;

        if (tgt.id === "abn" && value && abnPrevious !== value) {
            const abn = value.replaceAll(" ", "");

            if (abn && /^\d+$/.test(abn) && validateABN(abn)) {
                err = "Validating this ABN in the Australian Business Register, please wait...";

                //https://github.com/ABN-SFLookupTechnicalSupport/ABNLookupSampleCode
                await abnValidationRequest(`${apiUrls.abrSearch}&abn=${abn}`, "get", null, { noAuth: true, mode: "cors" })
                    .then(data => {
                        // The data string returned contains a JSON string enclosed between "callback(" and ")" so get the contents...
                        let result = data.substring(9, data.length - 1);
                        result = JSON.parse(result);

                        if (result.AbnStatus === "Active") {
                            let match = result.Abn.match(/^(\d{2})(\d{3})(\d{3})(\d{3})$/)
                            localAccount.Abn = `${match[1]} ${match[2]} ${match[3]} ${match[4]}`
                            abnPreviousSet(localAccount.abn);
                            err = null;

                            // let msg = `The following information was found in the Australian Business Register for the ABN provided;\n\nEntity Name: ${result.EntityName}\nRegistered: ${result.AbnStatusEffectiveFrom}`;
                            // message.Information("ABN Details", msg);
                        }

                        else {
                            err = "The ABN provided does not exist in the Australian Business Register.";
                        }
                    }, reject => {
                        err = "An error occurred looking up the ABN in the Australian Business Register.";
                    });
            }

            else {
                err = "This does not appear to be a valid ABN";
            }
        }

        if (control.length === 1) {
            localAccount.errors = {};
        }
        else {
            localAccount[control[0]].errors = {};
        }

        if (tgt.validity.valueMissing && value.length === 0) {
            if (tgt.id !== "abn" || (tgt.id === "abn" && !localAccount.noAbn)) {
                err = "This is a required field";
            }
        }

        if (tgt.type === "email" && value) {
            if (!emailHelper.isValidEmail(value)) err = "This does not appear to be a valid email address";
        }

        if (!err && tgt.id.endsWith("preferredNumber")) {
            if (!value || phoneHelper.isValid(value)) {
                localAccount[control[0]][control[1]] = phoneHelper.format(value);
            }
            else {
                err = "This does not appear to be a valid phone number.";
            }
        }

        if (tgt.id === "openingTime" || tgt.id === "closingTime") {
            err = null;
            const isValid = isValidSiteAccess(tgt.id, localAccount.openingTime, localAccount.closingTime);
            if (!isValid) return;
        }

        if (!!err) {
            if (control.length === 1) {
                localAccount.errors[control[0]] = err;
            }
            else {
                localAccount[control[0]].errors[control[1]] = err;
            }
        }

        localAccountSet(commonHelper.deepClone(localAccount));
    }

    /**
     * Checks ABN for validity using the published ABN checksum algorithm.
     * @param  {String|Number} value abn to validate
     * @return {Boolean} Is ABN Valid
     */
    const validateABN = (value) => {

        const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
        const abn = value.replace(/[^\d]/, '');
        let result = false;

        // check length is 11 digits
        if (abn.length === 11) {
            // Check the ABN complies with the ABR requirements (https://abr.business.gov.au/Help/AbnFormat)...
            let sum = 0, weight = 0;

            for (var index = 0; index <= weights.length - 1; index++) {
                weight = weights[index];
                const digit = abn[index] - (index ? 0 : 1);
                sum += weight * digit;
            }

            result = sum % 89 === 0;
        }

        return result;
    }

    // -- Render --------------------------------------------------------------------------------------------------------------------------
    const phoneFormatErrorMessage = (
        <>
            <label className="label-sx">Examples of valid phone numbers are:</label>
            <ul>
                <li>
                    <p>
                        <strong>National:</strong> 0712345678 or 07 1234 5678 or (07) 1234-5678
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Mobile:</strong> 0412345678 or 0412 345 678 or (0412) 345-678
                    </p>
                </li>
                <li>
                    <p>
                        <strong>Local Rate &amp; Freecall</strong>: 1800123456 or 1300 123-456 or 13 1234
                    </p>
                </li>
                <li>
                    <p>
                        <strong>International:</strong> 001161212345678 or 0011 61 2 12345678 or +61 2 12345678
                    </p>
                </li>
            </ul>
        </>
    )

    return (
        <AuthenticateUser>
            <div>
                {pageLoaded === null && <PageLoading message="Loading your profile data, please wait..." />}

                {!pageLoaded && pageError && (
                    <div className="text-center">
                        <h2 style={{ color: "red" }}>Error Loading Page</h2>
                        <div className="alert alert-danger">{pageError}</div>
                    </div>
                )}

                {pageLoaded && (
                    <>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <div>
                                    <img className={styles.icon} src={businessDetailsIcon} alt="Business Details Icon"></img>
                                    <span className={styles["card-title"]}>Business Details</span>
                                </div>

                                <div>
                                    {
                                        !businessReadOnly &&
                                        <>
                                            <button type="button" className="btn btn-danger btn-sm me-2" onClick={(evt) => displayCancelPrompt(evt, BUSINESS)} title="Cancel changes">
                                                <FontAwesomeIcon icon="window-close" /> Cancel
                                            </button>

                                            <button type="button" className="btn btn-success btn-sm" disabled={!isDirty || hasErrors(BUSINESS)} onClick={(evt) => saveProfile(evt, BUSINESS)} title="Save changes">
                                                <FontAwesomeIcon icon="save" /> Save
                                            </button>
                                        </>
                                    }
                                    {businessReadOnly && siteContactReadOnly && siteAddressReadOnly && billingContactReadOnly && billingAddressReadOnly
                                        && <FontAwesomeIcon icon="edit" className="hand" onClick={() => toggleReadOnlyGroup('Business', false)} title="Edit business details" />}
                                </div>
                            </CardHeader>

                            <CardBody className={styles["card-body"]}>
                                <Row>
                                    <Col lg={6} className="mb-4 mb-lg-0">
                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Business Name</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="accountName"
                                                    value={localAccount.accountName}
                                                    groupClass="business-name"
                                                    placeholder="Business Name"
                                                    mandatory={true}
                                                    size={controlSize}
                                                    readOnly={businessReadOnly}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group align-items-center">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label">Account Number</Label>
                                            </Col>

                                            <Col>
                                                <Input id="accountNumber" value={localAccount.accountNumber} placeholder="Account Number" mandatory={true} size={controlSize} readOnly={true} groupClass="account-number" />
                                            </Col>
                                        </Row>
                                    </Col>

                                    <Col lg={6}>
                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">ABN</Label>
                                            </Col>

                                            <Col>
                                                <Input id="abn" value={localAccount.abn} placeholder={localAccount.noAbn ? "" : "Please enter your 11-digit ABN"} size={controlSize} groupClass="abn" onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={businessReadOnly || localAccount.noAbn} error={localAccount.errors?.abn}>
                                                    {!localAccount.abn && !businessReadOnly &&
                                                        <div className="mt-2 ms-2">
                                                            <input
                                                                id="noAbn"
                                                                type="checkbox"
                                                                className="form-check-input ms-0"
                                                                checked={localAccount.noAbn}
                                                                disabled={businessReadOnly}
                                                                onChange={handleChange} />
                                                            <label className="form-check-label ms-1" style={{ fontWeight: "normal", fontStyle: "italic", fontSize: "0.85rem" }} htmlFor="siteAddress.notFound">
                                                                Continue without ABN
                                                            </label>
                                                        </div>
                                                    }
                                                </Input>
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Industry Type</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="industryCodeId"
                                                    type="select"
                                                    value={localAccount.industryCodeId}
                                                    placeholder="Select the industry type..."
                                                    mandatory={true}
                                                    size={controlSize}
                                                    groupClass="industry-type"
                                                    onChange={handleChange}
                                                    onBlur={validateInput}
                                                    readOnly={businessReadOnly}>

                                                    <option value="">Select the industry type...</option>

                                                    {industryCodes && industryCodes.map((option, index) => {
                                                        return (
                                                            <option key={index} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        );
                                                    })}
                                                </Input>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>

                        <Row className="form-row">
                            <Col lg={5}>
                                <Card className={styles["card-stretch"]}>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <img className={styles.icon} src={siteContactIcon} alt="Site Contact Icon"></img>
                                            <span className={styles["card-title"]}>Site Contact</span>
                                        </div>

                                        <div>
                                            {
                                                !siteContactReadOnly &&
                                                <>
                                                    <button type="button" className="btn btn-danger btn-sm me-2" onClick={(evt) => displayCancelPrompt(evt, SITE_CONTACT)} title="Cancel changes">
                                                        <FontAwesomeIcon icon="window-close" /> Cancel
                                                    </button>

                                                    <button type="button" className="btn btn-success btn-sm" disabled={!isDirty} onClick={(evt) => saveProfile(evt, SITE_CONTACT)} title="Save changes">
                                                        <FontAwesomeIcon icon="save" /> Save
                                                    </button>
                                                </>
                                            }

                                            {businessReadOnly && siteContactReadOnly && siteAddressReadOnly && billingContactReadOnly && billingAddressReadOnly
                                                && <FontAwesomeIcon icon="edit" className="hand" onClick={() => toggleReadOnlyGroup('Site Contact', false)} title="Edit site contact" />}
                                        </div>
                                    </CardHeader>

                                    <CardBody className={styles["card-body"]}>
                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">First Name</Label>
                                            </Col>

                                            <Col>
                                                <Input id="siteContact.firstName" value={localAccount.siteContact?.firstName} placeholder="Enter the first name" mandatory={true} size={controlSize} groupClass="firstName" error={localAccount.siteContact?.errors?.firstName} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={siteContactReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label">Last Name</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="siteContact.lastName"
                                                    placeholder="Enter the last name"
                                                    groupClass="lastName"
                                                    value={localAccount.siteContact?.lastName}
                                                    mandatory={false}
                                                    size={controlSize}
                                                    error={localAccount.siteContact?.errors?.lastName}
                                                    readOnly={siteContactReadOnly}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Email</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="siteContact.email"
                                                    type="email"
                                                    placeholder="Enter the email address"
                                                    groupClass="email"
                                                    value={localAccount.siteContact?.email}
                                                    size={controlSize}
                                                    error={localAccount.siteContact?.errors?.email}
                                                    readOnly={siteContactReadOnly}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Phone Number</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="siteContact.preferredNumber"
                                                    groupClass="preferred"
                                                    placeholder="Enter the preferred contact number"
                                                    value={localAccount.siteContact?.preferredNumber}
                                                    mandatory={true}
                                                    guide={false}
                                                    size={controlSize}
                                                    readOnly={siteContactReadOnly}
                                                    error={localAccount.siteContact?.errors?.preferredNumber}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput} />
                                            </Col>
                                        </Row>

                                        {
                                            (localAccount?.siteContact?.errors?.preferredNumber) && phoneFormatErrorMessage
                                        }

                                        {!siteContactReadOnly &&
                                            <>
                                                <p className="mandatory mb-0 fst-italic" style={{ fontSize: "0.85rem" }}> Mandatory field</p>
                                            </>
                                        }
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col lg={7}>
                                <Card className={styles["card-stretch"]}>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <img className={styles.icon} src={siteAddressIcon} alt="Site Address Icon"></img>
                                            <span className={styles["card-title"]}>Site Address</span>
                                        </div>

                                        <div>
                                            {
                                                !siteAddressReadOnly &&
                                                <>
                                                    <button type="button" className="btn btn-danger btn-sm me-2" onClick={(evt) => displayCancelPrompt(evt, SITE_ADDRESS)} title="Cancel changes">
                                                        <FontAwesomeIcon icon="window-close" /> Cancel
                                                    </button>

                                                    <button type="button" className="btn btn-success btn-sm" disabled={!isDirty} onClick={(evt) => saveProfile(evt, SITE_ADDRESS)} title="Save changes">
                                                        <FontAwesomeIcon icon="save" /> Save
                                                    </button>
                                                </>
                                            }

                                            {businessReadOnly && siteContactReadOnly && siteAddressReadOnly && billingContactReadOnly && billingAddressReadOnly &&
                                                <FontAwesomeIcon icon="edit" className="hand" onClick={() => toggleReadOnlyGroup(SITE_ADDRESS, false)} title="Edit site address" />
                                            }
                                        </div>
                                    </CardHeader>

                                    <CardBody className={`${styles["card-body"]}`}>
                                        <Row className="form-group">
                                            <Col>
                                                <Autocomplete
                                                    readOnly={siteAddressReadOnly}
                                                    ref={autoCompleteRef}
                                                    className="form-control"
                                                    placeholder="Enter your street address excluding lot, level or building details..."
                                                    disabled={localAccount.siteAddress.notFound}
                                                    apiKey={process.env.REACT_APP_GOOGLE_AUTOCOMPLETE_KEY}
                                                    options={{ componentRestrictions: { country: ["au", "nz"] }, types: ["address"] }}
                                                    onPlaceSelected={(place) => {
                                                        let unitNum, streetNum, streetName, suburb, state, postcode, country;

                                                        for (let i = 0; i < place.address_components.length; i++) {
                                                            switch (place.address_components[i].types[0]) {
                                                                case "subpremise":
                                                                    unitNum = place.address_components[i].long_name + "";
                                                                    break;

                                                                case "street_number":
                                                                    streetNum = place.address_components[i].long_name + "";
                                                                    break;

                                                                case "route":
                                                                    streetName = place.address_components[i].long_name;
                                                                    break;

                                                                case "locality":
                                                                case "administrative_area_level_3":
                                                                    suburb = place.address_components[i].long_name;
                                                                    break;

                                                                case "postal_code":
                                                                    postcode = place.address_components[i].long_name;
                                                                    break;

                                                                case "administrative_area_level_1":
                                                                    state = place.address_components[i].short_name;
                                                                    break;

                                                                case "country":
                                                                    country = place.address_components[i].long_name;
                                                                    break;
                                                                default:
                                                            }
                                                        }

                                                        const _localAccount = commonHelper.deepClone(localAccount);

                                                        _localAccount.siteAddress = {};
                                                        _localAccount.siteAddress.line1 = (unitNum ? `${unitNum}/` : "") + (streetNum || "") + " " + (streetName || "").trim();
                                                        _localAccount.siteAddress.suburb = suburb;
                                                        _localAccount.siteAddress.state = state;
                                                        _localAccount.siteAddress.postcode = postcode;
                                                        _localAccount.siteAddress.country = country;
                                                        _localAccount.siteAddress.latitude = place.geometry.location.lat();
                                                        _localAccount.siteAddress.longitude = place.geometry.location.lng();
                                                        localAccountSet(_localAccount);
                                                        mapCenterSet(place.geometry.location);

                                                        siteAddressIgnoreNumSet(false);
                                                        isDirtySet(true);
                                                        isValidSiteAddress(_localAccount.siteAddress);
                                                    }}
                                                />

                                                <div className="form-check ms-1 d-flex align-items-center" style={{ marginTop: "5px" }}>
                                                    <input type="checkbox" className="form-check-input mt-0" id="siteAddress.notFound" checked={localAccount.siteAddress.notFound} onChange={handleChange} disabled={siteAddressReadOnly} />
                                                    <label className="form-check-label ms-1" style={{ fontWeight: "normal", fontStyle: "italic", fontSize: "0.85rem" }} htmlFor="siteAddress.notFound">
                                                        Could not locate my address, please have someone contact me to assist.
                                                    </label>
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col lg={7}>
                                                <Row className="form-row form-group mt-2">
                                                    <Col lg={4} xl={3}>
                                                        <Label className="col-form-label">Address Detail</Label>
                                                    </Col>

                                                    <Col>
                                                        <Input
                                                            id="siteAddress.line2"
                                                            value={localAccount.siteAddress?.line2}
                                                            placeholder='E.g. "Unit 101" or "Level 1" or "Arundel Plaza" etc...'
                                                            size={controlSize}
                                                            groupClass="address2"
                                                            onFocus={handleFocus}
                                                            onChange={handleChange}
                                                            onBlur={validateInput}
                                                            readOnly={siteAddressReadOnly} />
                                                    </Col>
                                                </Row>

                                                <Row className="form-row form-group">
                                                    <Col lg={4} xl={3}>
                                                        <Label className="col-form-label mandatory">Address</Label>
                                                    </Col>

                                                    <Col>
                                                        <div id="siteAddress.line1" className="form-control" readOnly={true} style={{ "height": "auto" }}>
                                                            <div>
                                                                {localAccount.siteAddress?.line1}
                                                            </div>

                                                            <div>
                                                                {localAccount.siteAddress?.suburb}&nbsp;{localAccount.siteAddress?.state}&nbsp;{localAccount.siteAddress?.postcode}
                                                            </div>

                                                            {localAccount.siteAddress?.country}
                                                        </div>
                                                    </Col>
                                                </Row>

                                                <Row className="form-row form-group mt-2">
                                                    <Col lg={4} xl={3}>
                                                        <Label className="col-form-label mandatory">Bin Access Times</Label>
                                                    </Col>

                                                    <Col lg={8} xl={9}>
                                                        <div className="d-flex">
                                                            <Input
                                                                id="openingTime"
                                                                value={localAccount?.openingTime}
                                                                placeholder="Accessible from..."
                                                                mandatory={true}
                                                                size={controlSize}
                                                                type="masked"
                                                                mask={[/\d/, /\d/, ":", /\d/, /\d/]}
                                                                groupClass={`${styles["bin-access-time"]} mb-0`}
                                                                error={localAccount.errors?.openingTime}
                                                                prepend={
                                                                    <InputGroupText>
                                                                        <FontAwesomeIcon icon="clock" style={{ color: "green", transform: "scaleX(-1)" }} title="Open" />
                                                                    </InputGroupText>
                                                                }
                                                                onFocus={handleFocus}
                                                                onChange={handleChange}
                                                                onBlur={validateInput}
                                                                readOnly={siteAddressReadOnly}
                                                            />

                                                            <span className="align-self-center mx-2">to</span>

                                                            <Input
                                                                id="closingTime"
                                                                value={localAccount?.closingTime}
                                                                placeholder="Accessible to..."
                                                                mandatory={true}
                                                                size={controlSize}
                                                                type="masked"
                                                                mask={[/\d/, /\d/, ":", /\d/, /\d/]}
                                                                groupClass={`${styles["bin-access-time"]} mb-0`}
                                                                error={localAccount.errors?.closingTime}
                                                                prepend={
                                                                    <InputGroupText>
                                                                        <FontAwesomeIcon icon="clock" style={{ color: "darkred" }} title="Close" />
                                                                    </InputGroupText>
                                                                }
                                                                onFocus={handleFocus}
                                                                onChange={handleChange}
                                                                onBlur={validateInput}
                                                                readOnly={siteAddressReadOnly}
                                                            />
                                                        </div>

                                                        <p><em>Enter times in 24 hour format; e.g. 17:30 for 5:30pm</em></p>
                                                        <p><em>Bin access times indicate the times the bins are able to be accessed by our drivers. They do not indicate the times within which you would prefer the bins to be collected. Please allow a minimum of 4 hours between the times entered.</em></p>
                                                    </Col>
                                                </Row>
                                            </Col>

                                            <Col lg={5}>
                                                {!(localAccount.siteAddress && localAccount.siteAddress.latitude && localAccount.siteAddress.longitude) && (
                                                    <Card className={styles["card-stretch"]}>
                                                        <div className="d-flex flex-column justify-content-center align-items-center h-100 p-4">
                                                            <img
                                                                src="data:image/png;base64,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"
                                                                width="110"
                                                                height="100"
                                                                alt="File:Google Maps Logo 2020.svg - Wikimedia Commons"
                                                            />

                                                            <div className="text-center mt-4" style={{ color: "#e54800" }}>
                                                                Location data is missing for this address
                                                            </div>
                                                        </div>
                                                    </Card>
                                                )}

                                                {localAccount.siteAddress && localAccount.siteAddress.latitude && localAccount.siteAddress.longitude && (
                                                    <Card className={styles.map + " h-100"} style={{ borderWidth: "0" }}>
                                                        <Map
                                                            google={props.google} zoom={18}
                                                            style={{ position: "static", height: "100%", borderRadius: "1rem", border: "2.5px solid var(--branding-feather-grey)" }}
                                                            initialCenter={{ lat: localAccount.siteAddress.latitude, lng: localAccount.siteAddress.longitude }} center={mapCenter}>
                                                            <Marker name={localAccount.siteAddress?.line1} position={mapCenter} />
                                                        </Map>
                                                    </Card>
                                                )}
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                        <Row className="form-row">
                            <Col lg={5}>
                                <Card className={styles["card-stretch"]}>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <img className={styles.icon} src={billingContactIcon} alt="Billing Contact Icon"></img>
                                            <span className={styles["card-title"]}>Billing Contact</span>
                                        </div>

                                        <div>
                                            {
                                                !billingContactReadOnly &&
                                                <>
                                                    <button type="button" className="btn btn-danger btn-sm me-2" onClick={(evt) => displayCancelPrompt(evt, BILLING_CONTACT)} title="Cancel changes">
                                                        <FontAwesomeIcon icon="window-close" /> Cancel
                                                    </button>

                                                    <button type="button" className="btn btn-success btn-sm" disabled={!isDirty} onClick={(evt) => saveProfile(evt, BILLING_CONTACT)} title="Save changes">
                                                        <FontAwesomeIcon icon="save" /> Save
                                                    </button>
                                                </>
                                            }
                                            {businessReadOnly && siteContactReadOnly && siteAddressReadOnly && billingContactReadOnly && billingAddressReadOnly
                                                && <FontAwesomeIcon icon="edit" className="hand" onClick={() => toggleReadOnlyGroup('Billing Contact', false)} title="Edit billing contact" />}
                                        </div>
                                    </CardHeader>

                                    <CardBody className={styles["card-body"]}>
                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">First Name</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingContact.firstName" value={localAccount.billingContact?.firstName} placeholder="Enter the first name" mandatory={true} size={controlSize} groupClass="firstName" error={localAccount.billingContact?.errors?.firstName} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingContactReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label">Last Name</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingContact.lastName" value={localAccount.billingContact?.lastName} placeholder="Enter the last name" mandatory={true} size={controlSize} groupClass="lastName" error={localAccount.billingContact?.errors?.lastName} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingContactReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Email</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="billingContact.email"
                                                    type="email"
                                                    placeholder="Enter the email address"
                                                    groupClass="email"
                                                    oneOf={true}
                                                    value={localAccount.billingContact?.email}
                                                    size={controlSize}
                                                    error={localAccount.billingContact?.errors?.email}
                                                    readOnly={billingContactReadOnly}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={4} xl={3}>
                                                <Label className="col-form-label mandatory">Phone Number</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingContact.preferredNumber" placeholder="Enter the preferred contact number" value={localAccount.billingContact?.preferredNumber} mandatory={true} size={controlSize} groupClass="preferred" error={localAccount.billingContact?.errors?.preferredNumber} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingContactReadOnly} />
                                            </Col>
                                        </Row>

                                        {(localAccount?.billingContact?.errors?.preferredNumber) && phoneFormatErrorMessage}


                                        {!billingContactReadOnly &&
                                            <>
                                                <p className="mandatory mb-0 fst-italic" style={{ fontSize: "0.85rem" }}> Mandatory field</p>
                                            </>
                                        }
                                    </CardBody>
                                </Card>
                            </Col>

                            <Col lg={7}>
                                <Card className={styles["card-stretch"]}>
                                    <CardHeader className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <img className={styles.icon} src={billingAddressIcon} alt="Billing Address Icon"></img>
                                            <span className={styles["card-title"]}>Billing Address</span>
                                        </div>
                                        <div>
                                            {
                                                !billingAddressReadOnly &&
                                                <>
                                                    <button type="button" className="btn btn-danger btn-sm me-2" onClick={(evt) => displayCancelPrompt(evt, BILLING_ADDRESS)} title="Cancel changes">
                                                        <FontAwesomeIcon icon="window-close" /> Cancel
                                                    </button>

                                                    <button type="button" className="btn btn-success btn-sm" disabled={!isDirty} onClick={(evt) => saveProfile(evt, BILLING_ADDRESS)} title="Save changes">
                                                        <FontAwesomeIcon icon="save" /> Save
                                                    </button>
                                                </>
                                            }
                                            {businessReadOnly && siteContactReadOnly && siteAddressReadOnly && billingContactReadOnly && billingAddressReadOnly
                                                && <FontAwesomeIcon icon="edit" className="hand" onClick={() => toggleReadOnlyGroup('Billing Address', false)} title="Edit billing address" />}
                                        </div>
                                    </CardHeader>

                                    <CardBody className={styles["card-body"]}>
                                        <Row className="form-row form-group">
                                            <Col lg={2} xl={1}>
                                                <Label className="col-form-label mandatory">Line 1</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingAddress.line1" value={localAccount.billingAddress?.line1} placeholder="123 Smith Street" mandatory={true} size={controlSize} groupClass="address1" error={localAccount.billingAddress?.errors?.line1} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingAddressReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={2} xl={1}>
                                                <Label className="col-form-label">Line 2</Label>
                                            </Col>

                                            <Col>
                                                <Input
                                                    id="billingAddress.line2"
                                                    value={localAccount.billingAddress?.line2}
                                                    placeholder="Level 1, Arundel Plaza, etc..." size={controlSize}
                                                    onFocus={handleFocus}
                                                    onChange={handleChange}
                                                    onBlur={validateInput}
                                                    readOnly={billingAddressReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={2} xl={1}>
                                                <Label className="col-form-label mandatory">Suburb</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingAddress.suburb" value={localAccount.billingAddress?.suburb} placeholder="E.g. Brisbane, Melbourne, Parramatta..." mandatory={true} size={controlSize} groupClass="suburb" error={localAccount.billingAddress?.errors?.suburb} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingAddressReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={2} xl={1}>
                                                <Label className="col-form-label mandatory">State</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingAddress.state" value={localAccount.billingAddress?.state} placeholder="E.g. Queensland, New South Wales, Victoria..." mandatory={true} size={controlSize} groupClass="state" error={localAccount.billingAddress?.errors?.state} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingAddressReadOnly} />
                                            </Col>
                                        </Row>

                                        <Row className="form-row form-group">
                                            <Col lg={2} xl={1}>
                                                <Label className="col-form-label mandatory">Postcode</Label>
                                            </Col>

                                            <Col>
                                                <Input id="billingAddress.postcode" value={localAccount.billingAddress?.postcode} placeholder="E.g. 4000, 2000, 3000..." mandatory={true} size={controlSize} groupClass="postcode" error={localAccount.billingAddress?.errors?.postcode} type="masked" mask={[/\d/, /\d/, /\d/, /\d/]} onFocus={handleFocus} onChange={handleChange} onBlur={validateInput} readOnly={billingAddressReadOnly} />
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                <ModalPrompt modalTitle={cancelTitle} modalMessage={cancelMessage} isDisplayed={displayCancel} handleYes={cancelUpdate} handleNo={() => displayCancelSet(false)} />

                {loader}
                {NavigationPrompt}
            </div>
        </AuthenticateUser>
    );
};

export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
})(ProfilePage)
